import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { Box, Flex, FlexColumn, Tag, Text } from 'core/components';
import { Field, FormDialog, InputGroup, Select, Switch } from 'core/form';
import { FieldLabel } from 'core/form/components/FieldLabel';
import { POLICY_APPLICATION_LABELS } from '@kentik/ui-shared/alerting/constants';
import NotificationsSelect from 'app/components/notifications/NotificationsSelect';
import RunbookOptionRenderer from 'app/components/runbooks/RunbookOptionRenderer';
import RunbookValueTagRenderer from 'app/components/runbooks/RunbookValueTagRenderer';

const fields = {
  rule_id: {
    label: 'Policy',
    rules: 'required',
    defaultValue: null,
    placeholder: 'Select a policy to configure'
  },
  enabled: {
    defaultValue: true,
    helpText: 'Enable or disable automatic investigations for this policy'
  },
  delay_minutes: {
    label: 'Investigation Delay (minutes)',
    rules: 'required|integer|min:0|max:1440',
    defaultValue: 5,
    placeholder: 'Enter delay in minutes (0-1440)',
    helpText: 'Time to wait before starting automatic investigation after an alarm is triggered'
  },
  notification_channel_ids: {
    label: 'Notification Channels',
    defaultValue: [],
    placeholder: 'Select notification channels for investigation results'
  },
  runbook_ids: {
    label: 'Runbooks',
    defaultValue: [],
    placeholder: 'Select runbooks to use during investigation'
  }
};

const policyOptionFormatter = (option) => (
  <FlexColumn>
    <Text>{option.label}</Text>
    <Text muted small>
      {POLICY_APPLICATION_LABELS[option.application] || option.application}
    </Text>
  </FlexColumn>
);

@inject('$notifications', '$alerting', '$runbook', '$investigationConfig')
@observer
class AutoInvestigationFormDialog extends Component {
  componentDidMount() {
    const { $runbook } = this.props;
    if (!$runbook.collection.hasFetched) {
      $runbook.collection.fetch();
    }
  }

  @computed
  get policyOptions() {
    const { $alerting, $investigationConfig } = this.props;

    if (!$alerting.policyCollection.hasFetched || !$investigationConfig.collection.hasFetched) {
      return [];
    }

    // Get the IDs of policies already opted into auto-investigation
    const configuredRuleIds = new Set(Object.keys($investigationConfig.collection.modelById));

    // To filter policies without mutating the collection
    const allPolicies = $alerting.policyCollection.get();

    return allPolicies
      .filter((policy) => policy.ruleId && !configuredRuleIds.has(policy.ruleId))
      .map((policy) => ({
        value: policy.ruleId,
        label: policy.name,
        application: policy.application
      }));
  }

  @computed
  get runbookOptions() {
    const { $runbook } = this.props;
    return $runbook.runbookSelectOptions;
  }

  handlePolicyChange = ({ value: ruleId, form }) => {
    if (!ruleId) {
      return;
    }

    const { $runbook, $alerting } = this.props;
    const runbookIdsField = form.getField('runbook_ids');

    // Only prepopulate if runbooks field is empty
    if (runbookIdsField.value.length === 0) {
      // Get the policy object to pass to getRunbooksForPolicy
      const policyModel = $alerting.policyCollection.getPolicyByRuleId(ruleId);
      if (!policyModel) {
        return;
      }

      const mappedRunbookIds = $runbook.getRunbooksForPolicy(policyModel).map((runbook) => runbook.get('id'));

      if (mappedRunbookIds.length > 0) {
        runbookIdsField.setValue(mappedRunbookIds);
      }
    }
  };

  get formContent() {
    const { selectedConfig, $alerting } = this.props;
    const isEditing = !selectedConfig.isNew;
    const policyModel = isEditing ? $alerting.policyCollection.getPolicyByRuleId(selectedConfig.get('rule_id')) : null;

    return (
      <>
        {isEditing ? (
          <Box mb={3}>
            <FieldLabel fontWeight={600} mb={1}>
              Policy
            </FieldLabel>
            <FlexColumn p={1} borderRadius="4px" border="thin">
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="bold" fontSize="15px">
                  {policyModel?.get('name') || 'Unknown Policy'}
                </Text>
                <Tag intent="primary" minimal>
                  {POLICY_APPLICATION_LABELS[policyModel?.get('application')] || 'Unknown Type'}
                </Tag>
              </Flex>
              {policyModel?.get('description') && (
                <Text muted small>
                  {policyModel?.get('description')}
                </Text>
              )}
            </FlexColumn>
          </Box>
        ) : (
          <Box mb={3}>
            <Field name="rule_id" onChange={this.handlePolicyChange} options={this.policyOptions}>
              <Select fill showFilter optionFormatter={policyOptionFormatter} />
            </Field>
          </Box>
        )}

        <Box mb={3}>
          <Field name="enabled" showLabel={false}>
            <Switch large switchLabel="Enabled" />
          </Field>
        </Box>

        <Field name="delay_minutes">
          <InputGroup type="number" />
        </Field>

        <Box mt={3}>
          <NotificationsSelect
            showAddButton
            showTestButton={false}
            fieldName="notification_channel_ids"
            fieldProps={{
              label: 'Notification Channels',
              placeholder: 'Select notification channels for investigation results',
              showLabel: true,
              width: '100%'
            }}
          />
        </Box>

        <Box mt={3}>
          <Field
            name="runbook_ids"
            options={this.runbookOptions}
            helpText="Select runbooks that will be used to guide the AI during investigation"
          >
            <Select
              multi
              fill
              showFilter
              placeholder="Select runbooks..."
              optionRenderer={RunbookOptionRenderer}
              valueTagRenderer={RunbookValueTagRenderer}
            />
          </Field>
        </Box>
      </>
    );
  }

  render() {
    const { isOpen, selectedConfig, onClose, onSave } = this.props;
    const isEditing = !selectedConfig.isNew;

    return (
      <FormDialog
        isOpen={isOpen}
        entityName="Configuration"
        onClose={onClose}
        width={500}
        fields={fields}
        model={selectedConfig}
        options={{ name: 'autoInvestigation' }}
        formComponentProps={{
          large: true,
          onLoad: (form) => {
            if (isEditing) {
              form.getField('rule_id').setRules('');
            } else {
              form.getField('rule_id').setRules('required');
            }
          }
        }}
        formActionsProps={{
          onCancel: onClose,
          onSubmit: onSave
        }}
      >
        {this.formContent}
      </FormDialog>
    );
  }
}

export default AutoInvestigationFormDialog;
